import React from "react";

import { ImpersonateStatus, useImpersonate } from "../../../hooks/impersonate";
import { useMe } from "../../../hooks/queries";
import { AccountType } from "../../../models/AccountData";
import { getEnvironment } from "../../../utils/getEnvironment";

interface IOptionalRenderProps {
  children?: React.ReactNode;
  /**
   * @description The user must have at least one of these roles. If provided, the children will only be rendered if the user has at least one of these roles.
   * @example
   * // User must be either loc_admin or org_admin to see <Content />
   * <OptionalRender mustBeOneOfRoles={[AccountType.LOC_ADMIN, AccountType.ORG_ADMIN]}>
   *   <Content />
   * </OptionalRender>
   */
  mustBeOneOfRoles?: AccountType[];
  /**
   * @description The user must not have any of these roles. If provided, the children will only be rendered if the user does not have any of these roles.
   * @example
   * // User must not be admin to see <Content />
   * <OptionalRender mustNotBeRoles={[AccountType.ADMIN]}>
   *   <Content />
   * </OptionalRender>
   */
  mustNotBeRoles?: AccountType[];
  /**
   * @description Must be in an impersonation state to render the children.
   * @example
   * // User must be in impersonation state to see <Content />
   * <OptionalRender mustBeImpersonating={true}>
   *   <Content />
   * </OptionalRender>
   */
  mustBeImpersonating?: boolean;
  /**
   * @description List of conditions that must be true. All conditions must be true to render the children.
   * @example
   * // process.env.FEATURE_FLAG_EXAMPLE must be true to see <Content />
   * <OptionalRender mustBeTrue={[process.env.FEATURE_FLAG_EXAMPLE]}>
   *   <Content />
   * </OptionalRender>
   */
  mustBeTrue?: boolean | boolean[];
  /**
   * @description List of conditions that must be false. All conditions must be false to render the children.
   * @example
   * // process.env.FEATURE_FLAG_EXAMPLE must be false to see <Content />
   * <OptionalRender mustBeFalse={[process.env.FEATURE_FLAG_EXAMPLE]}>
   *   <Content />
   * </OptionalRender>
   */
  mustBeFalse?: boolean | boolean[];
  /**
   * @description The user must be in the specified environment to render the children. Should only be used temporarily when acting as a feature flag.
   * @example
   * // User must be in local environment to see <Content />
   * <OptionalRender mustBeEnvironment="local">
   *   <Content />
   * </OptionalRender>
   * @example
   * // User must be in local or staging environment to see <Content />
   * <OptionalRender mustBeEnvironment={["local", "staging"]}>
   *   <Content />
   * </OptionalRender>
   */
  mustBeEnvironment?:
    | "local"
    | "staging"
    | "production"
    | ("local" | "staging" | "production")[];
  /**
   * @description Custom condition function. Must return true to render the children.
   * @example
   * // Custom condition must return true to see <Content />
   * <OptionalRender customCondition={() => userHasAccess()}>
   *   <Content />
   * </OptionalRender>
   */
  customCondition?: () => boolean;
}

/**
 * A conditional rendering component that can decide whether to show children based on various conditions.
 */
export const OptionalRender: React.FunctionComponent<IOptionalRenderProps> = ({
  children,
  mustBeOneOfRoles,
  mustNotBeRoles,
  mustBeImpersonating,
  mustBeTrue,
  mustBeFalse,
  mustBeEnvironment,
  customCondition,
}) => {
  const { data: me } = useMe();
  const { impersonateStatus } = useImpersonate();

  // If no conditions are provided, render the children directly
  if (
    mustBeOneOfRoles === undefined &&
    mustNotBeRoles === undefined &&
    mustBeImpersonating === undefined &&
    mustBeTrue === undefined &&
    mustBeFalse === undefined &&
    customCondition === undefined &&
    mustBeEnvironment === undefined
  ) {
    return <>{children}</>;
  }

  // Check if the user has at least one of the required roles
  if (mustBeOneOfRoles !== undefined && mustBeOneOfRoles.length > 0) {
    // Wait until me is loaded to make a decision
    if (me === undefined) {
      return null;
    }

    if (!mustBeOneOfRoles.includes(me.type)) {
      return null;
    }
  }

  // Check if the user has none of the forbidden roles
  if (mustNotBeRoles !== undefined && mustNotBeRoles.length > 0) {
    // Wait until me is loaded to make a decision
    if (me === undefined) {
      return null;
    }

    if (mustNotBeRoles.includes(me.type)) {
      return null;
    }
  }

  // Check impersonation state
  if (mustBeImpersonating !== undefined) {
    const isImpersonating = impersonateStatus === ImpersonateStatus.IMPERSONATE;
    if (mustBeImpersonating !== isImpersonating) {
      return null;
    }
  }

  // Check conditions that must be true
  if (mustBeTrue !== undefined) {
    if (Array.isArray(mustBeTrue)) {
      if (!mustBeTrue.every((condition) => condition === true)) {
        return null;
      }
    } else if (mustBeTrue !== true) {
      return null;
    }
  }

  // Check conditions that must be false
  if (mustBeFalse !== undefined) {
    if (Array.isArray(mustBeFalse)) {
      if (!mustBeFalse.every((condition) => condition === false)) {
        return null;
      }
    } else if (mustBeFalse !== false) {
      return null;
    }
  }

  if (mustBeEnvironment !== undefined) {
    const environment = getEnvironment();
    const allowedEnvironments = Array.isArray(mustBeEnvironment)
      ? mustBeEnvironment
      : [mustBeEnvironment];

    if (!allowedEnvironments.includes(environment)) {
      return null;
    }
  }

  // Check custom condition
  if (customCondition !== undefined && !customCondition()) {
    return null;
  }

  // All conditions are met, render the children
  return <>{children}</>;
};
